/**
 * Signal tracing through the effective wiring
 * (docs/wiring-documentation-proposal.md, "Using it in config UIs").
 *
 * Links say which connector feeds which; the routes inside routers come from
 * their state: Videohub crosspoints (`/outputs/N/source`, the 0-based input
 * index) and ATEM aux sources (`/auxiliaries/N/source`, an ATEM input ID,
 * matched to ports by their `atem_input` control). With both, a signal can
 * be followed across devices:
 *
 *   DL1 ─link→ VH in 21 ─[VH route]→ VH out 6 ─link→ ATEM in 6 "Ebike / Nicolas"
 *
 * `routes` is the device state by device ID: live state from the bridge
 * while the config runs, else the config's stored `device_states`. A
 * bidirectional port (DeckLink SDI) has the direction its link gives it: it
 * is an output when it is the link's `from`, an input when it is its `to`.
 *
 * Pure helpers, used by the bridge and the frontend.
 */
import { portLabel, } from "./wiring.js";
// ─── Router state ─────────────────────────────────────────────
function stateValue(routes, deviceID, path) {
    return routes[deviceID]?.values?.[path];
}
function numberValue(routes, deviceID, path) {
    const value = stateValue(routes, deviceID, path);
    if (value === undefined || value === "" || typeof value === "boolean") {
        return undefined;
    }
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
}
/** The label the device itself has for a port ("Ebike / Nicolas"). */
export function portHardwareLabel(port, routes) {
    const control = port.control;
    if (!control)
        return null;
    const path = control.kind === "videohub_input"
        ? `/inputs/${control.index}/label`
        : control.kind === "videohub_output"
            ? `/outputs/${control.index}/label`
            : control.kind === "atem_input"
                ? `/inputs/${control.input_id}/longName`
                : control.kind === "atem_aux"
                    ? `/auxiliaries/${control.index}/longName`
                    : null;
    if (!path)
        return null;
    const value = stateValue(routes, port.deviceID, path);
    return typeof value === "string" && value.trim() ? value : null;
}
/**
 * Names of ATEM sources that are not connectors. Inputs 1–999 are physical
 * (documented as ports); the rest are generated inside the switcher.
 */
export function atemInternalSourceName(sourceID) {
    if (sourceID === 0)
        return "Black";
    if (sourceID === 1000)
        return "Colour bars";
    if (sourceID > 2000 && sourceID < 2100) {
        return `Colour generator ${sourceID - 2000}`;
    }
    if (sourceID >= 3010 && sourceID < 4000) {
        const player = Math.floor((sourceID - 3000) / 10);
        return `Media player ${player}${sourceID % 10 === 1 ? " key" : ""}`;
    }
    if (sourceID >= 6000 && sourceID < 6100) {
        return `SuperSource ${sourceID === 6000 ? 1 : sourceID - 6000}`;
    }
    if (sourceID > 7000 && sourceID < 7100) {
        return `Clean feed ${sourceID - 7000}`;
    }
    if (sourceID > 8000 && sourceID < 8100) {
        return `Auxiliary ${sourceID - 8000}`;
    }
    if (sourceID >= 9000 && sourceID < 9100) {
        return `Multiviewer ${sourceID - 9000 || 1}`;
    }
    if (sourceID >= 10010 && sourceID < 11000) {
        const me = Math.floor((sourceID - 10000) / 10);
        return `M/E ${me} ${sourceID % 10 === 1 ? "preview" : "program"}`;
    }
    return `Source ${sourceID}`;
}
export function atemSourceName(routes, deviceID, sourceID) {
    const label = stateValue(routes, deviceID, `/inputs/${sourceID}/longName`);
    return typeof label === "string" && label.trim()
        ? label
        : atemInternalSourceName(sourceID);
}
// ─── Wiring lookups ───────────────────────────────────────────
class TraceIndex {
    wiring;
    portByID;
    deviceByID;
    portsByDevice = new Map();
    constructor(wiring) {
        this.wiring = wiring;
        this.portByID = new Map(wiring.ports.map((port) => [port.id, port]));
        this.deviceByID = new Map(wiring.devices.map((device) => [device.id, device]));
        for (const port of wiring.ports) {
            const list = this.portsByDevice.get(port.deviceID) ?? [];
            list.push(port);
            this.portsByDevice.set(port.deviceID, list);
        }
    }
    device(port) {
        return this.deviceByID.get(port.deviceID) ?? null;
    }
    /** The router port with a control address on the same device. */
    controlled(deviceID, match) {
        return this.portsByDevice
            .get(deviceID)
            ?.find((port) => !!port.control && match(port.control));
    }
    /**
     * Whether the signal arrives at `port` over its cable (input, or the
     * `to` end of a bidirectional port's link) rather than leaving it. An
     * uncabled bidirectional port counts as `uncabled` (arriving when asking
     * what feeds it, leaving when asking where it goes).
     */
    arriving(port, uncabled) {
        if (port.direction === "input")
            return true;
        if (port.direction === "output")
            return false;
        const link = this.wiring.linkByPort.get(port.id);
        return link ? link.toPortID === port.id : uncabled;
    }
    noLinkStop(port) {
        const suspended = this.wiring.suspendedPorts.get(port.id);
        const label = portLabel(port);
        return suspended
            ? {
                kind: "stop",
                reason: "suspended",
                text: `${label} is not connected (${suspended.reason})`,
                portID: port.id,
            }
            : {
                kind: "stop",
                reason: "not_cabled",
                text: `${label} is not cabled`,
                portID: port.id,
            };
    }
    portHop(port, routes, via = "origin") {
        return {
            kind: "port",
            port,
            device: this.device(port),
            via,
            hardwareLabel: portHardwareLabel(port, routes),
        };
    }
}
/** What feeds a port that a signal leaves (a router output, an aux, ...). */
function routeInto(index, port, routes) {
    const control = port.control;
    const device = index.device(port);
    const deviceName = device?.name ?? "the device";
    if (control?.kind === "videohub_output") {
        const path = `/outputs/${control.index}/source`;
        const source = numberValue(routes, port.deviceID, path);
        if (source === undefined) {
            return {
                kind: "stop",
                reason: "route_unknown",
                text: `Route of ${deviceName} ${portLabel(port)} unknown`,
            };
        }
        const input = index.controlled(port.deviceID, (other) => other.kind === "videohub_input" && other.index === source);
        if (!input) {
            return {
                kind: "stop",
                reason: "undocumented_port",
                text: `${deviceName} routes input ${source + 1}, which has no documented port`,
            };
        }
        return { kind: "port", port: input, path };
    }
    if (control?.kind === "atem_aux") {
        const path = `/auxiliaries/${control.index}/source`;
        const source = numberValue(routes, port.deviceID, path);
        if (source === undefined) {
            return {
                kind: "stop",
                reason: "route_unknown",
                text: `Source of ${deviceName} ${portLabel(port)} unknown`,
            };
        }
        const input = index.controlled(port.deviceID, (other) => other.kind === "atem_input" && other.input_id === source);
        if (input)
            return { kind: "port", port: input, path };
        if (source >= 1 && source < 1000) {
            return {
                kind: "stop",
                reason: "undocumented_port",
                text: `${deviceName} input ${source} has no documented port`,
            };
        }
        return {
            kind: "internal",
            device,
            sourceID: source,
            name: atemSourceName(routes, port.deviceID, source),
            path,
        };
    }
    if (control?.kind === "atem_multiviewer") {
        return {
            kind: "internal",
            device,
            // ATEM source IDs of the multiviewers
            sourceID: 9001 + control.index,
            name: `Multiviewer ${control.index + 1}`,
        };
    }
    // Devices without routing knowledge: the signal starts here
    return { kind: "origin" };
}
// ─── Upstream ─────────────────────────────────────────────────
/**
 * "What feeds X?": the chain of hops that ends at `portID`, in signal-flow
 * order (the source first, `portID` last). Follows links backwards and,
 * inside routers, the current route. When the trace cannot go further for
 * another reason than reaching a source (no cable, re-patched away, route
 * unknown, a loop), the chain starts with a `stop` hop that says why.
 */
export function resolveUpstream(portID, wiring, routes = {}) {
    const index = new TraceIndex(wiring);
    const start = index.portByID.get(portID);
    if (!start) {
        return [
            {
                kind: "stop",
                reason: "unknown_port",
                text: "The port is not part of this studio's wiring",
                portID,
            },
        ];
    }
    // Built target first, reversed at the end
    const reversed = [];
    const seen = new Set();
    let port = start;
    let arriving = index.arriving(start, true);
    for (;;) {
        if (seen.has(port.id)) {
            reversed.push({
                kind: "stop",
                reason: "cycle",
                text: `The signal loops back to ${portLabel(port)}`,
                portID: port.id,
            });
            break;
        }
        seen.add(port.id);
        const hop = index.portHop(port, routes);
        reversed.push(hop);
        if (arriving) {
            const link = wiring.linkByPort.get(port.id);
            if (!link || link.toPortID !== port.id) {
                reversed.push(index.noLinkStop(port));
                break;
            }
            hop.via = "link";
            hop.link = link;
            const from = index.portByID.get(link.fromPortID);
            if (!from) {
                reversed.push({
                    kind: "stop",
                    reason: "unknown_port",
                    text: "The cable comes from a port that is not in the wiring",
                    portID: link.fromPortID,
                });
                break;
            }
            port = from;
            arriving = false;
            continue;
        }
        const inside = routeInto(index, port, routes);
        if (inside.kind === "origin")
            break;
        if (inside.kind === "stop") {
            reversed.push({ ...inside, portID: port.id });
            break;
        }
        hop.via = "route";
        hop.routePath = inside.path;
        if (inside.kind === "internal") {
            reversed.push({
                kind: "internal",
                device: inside.device,
                sourceID: inside.sourceID,
                name: inside.name,
                via: "origin",
            });
            break;
        }
        port = inside.port;
        arriving = true;
    }
    return reversed.reverse();
}
// ─── Downstream ───────────────────────────────────────────────
/**
 * "Where does X go?": every path from `portID` to where the signal ends (an
 * input of a device without routing, an ATEM M/E program, an output nobody
 * cabled), in signal-flow order, each starting at `portID`. Routers fan out:
 * a Videohub input routed to three outputs gives three paths. Each path ends
 * with a `stop` hop when it ends for another reason than reaching a device.
 */
export function resolveDownstream(portID, wiring, routes = {}, options = {}) {
    const index = new TraceIndex(wiring);
    const maxPaths = options.maxPaths ?? 100;
    const start = index.portByID.get(portID);
    if (!start) {
        return [
            [
                {
                    kind: "stop",
                    reason: "unknown_port",
                    text: "The port is not part of this studio's wiring",
                    portID,
                },
            ],
        ];
    }
    const paths = [];
    const walk = (port, arriving, path, seen) => {
        if (paths.length >= maxPaths)
            return;
        if (seen.has(port.id)) {
            paths.push([
                ...path,
                {
                    kind: "stop",
                    reason: "cycle",
                    text: `The signal loops back to ${portLabel(port)}`,
                    portID: port.id,
                },
            ]);
            return;
        }
        const nextSeen = new Set(seen).add(port.id);
        if (!arriving) {
            const link = wiring.linkByPort.get(port.id);
            if (!link || link.fromPortID !== port.id) {
                paths.push([...path, index.noLinkStop(port)]);
                return;
            }
            const to = index.portByID.get(link.toPortID);
            if (!to) {
                paths.push([
                    ...path,
                    {
                        kind: "stop",
                        reason: "unknown_port",
                        text: "The cable goes to a port that is not in the wiring",
                        portID: link.toPortID,
                    },
                ]);
                return;
            }
            const hop = index.portHop(to, routes, "link");
            hop.link = link;
            walk(to, true, [...path, hop], nextSeen);
            return;
        }
        // Arriving at a device: routers pass it on, other devices end it
        const control = port.control;
        if (control?.kind === "videohub_input") {
            const outputs = (index.portsByDevice.get(port.deviceID) ?? [])
                .filter((other) => other.control?.kind === "videohub_output")
                .sort((a, b) => a.number - b.number);
            if (!routes[port.deviceID]) {
                paths.push([
                    ...path,
                    {
                        kind: "stop",
                        reason: "route_unknown",
                        text: `Routes of ${index.device(port)?.name ?? "the router"} unknown`,
                        portID: port.id,
                    },
                ]);
                return;
            }
            const routed = outputs.filter((output) => {
                const outputControl = output.control;
                return (numberValue(routes, port.deviceID, `/outputs/${outputControl.index}/source`) === control.index);
            });
            if (routed.length === 0) {
                paths.push([...path, unroutedStop(port)]);
                return;
            }
            for (const output of routed) {
                const hop = index.portHop(output, routes, "route");
                hop.routePath = `/outputs/${output.control.index}/source`;
                walk(output, false, [...path, hop], nextSeen);
            }
            return;
        }
        if (control?.kind === "atem_input") {
            if (!routes[port.deviceID]) {
                paths.push([
                    ...path,
                    {
                        kind: "stop",
                        reason: "route_unknown",
                        text: `State of ${index.device(port)?.name ?? "the switcher"} unknown`,
                        portID: port.id,
                    },
                ]);
                return;
            }
            const branches = [];
            const auxes = (index.portsByDevice.get(port.deviceID) ?? [])
                .filter((other) => other.control?.kind === "atem_aux")
                .sort((a, b) => a.number - b.number);
            for (const aux of auxes) {
                const auxIndex = aux.control.index;
                const path_ = `/auxiliaries/${auxIndex}/source`;
                if (numberValue(routes, port.deviceID, path_) !== control.input_id) {
                    continue;
                }
                branches.push(() => {
                    const hop = index.portHop(aux, routes, "route");
                    hop.routePath = path_;
                    walk(aux, false, [...path, hop], nextSeen);
                });
            }
            for (let me = 0; me < 8; me++) {
                const programPath = `/mixEffects/${me}/programInput`;
                if (numberValue(routes, port.deviceID, programPath) !== control.input_id) {
                    continue;
                }
                branches.push(() => paths.push([
                    ...path,
                    {
                        kind: "internal",
                        device: index.device(port),
                        sourceID: 10010 + me * 10,
                        name: `M/E ${me + 1} program`,
                        via: "route",
                        routePath: programPath,
                    },
                ]));
            }
            if (branches.length === 0) {
                paths.push([...path, unroutedStop(port)]);
                return;
            }
            for (const branch of branches)
                branch();
            return;
        }
        paths.push(path);
    };
    const startArriving = index.arriving(start, false);
    walk(start, startArriving, [index.portHop(start, routes)], new Set());
    return paths;
}
function unroutedStop(port) {
    return {
        kind: "stop",
        reason: "unrouted",
        text: `${portLabel(port)} is not routed to any output`,
        portID: port.id,
    };
}
/** Can `input` be routed to `output` inside their device, and how? */
function routeChange(index, input, output) {
    const inControl = input.control;
    const outControl = output.control;
    if (!inControl || !outControl || input.deviceID !== output.deviceID) {
        return null;
    }
    if (inControl.kind === "videohub_input" &&
        outControl.kind === "videohub_output") {
        return {
            device: index.device(output),
            deviceID: output.deviceID,
            input,
            output,
            path: `/outputs/${outControl.index}/source`,
            value: inControl.index,
        };
    }
    if (inControl.kind === "atem_input" && outControl.kind === "atem_aux") {
        return {
            device: index.device(output),
            deviceID: output.deviceID,
            input,
            output,
            path: `/auxiliaries/${outControl.index}/source`,
            value: inControl.input_id,
        };
    }
    return null;
}
/**
 * "Put DL1 on ATEM input 6": the router settings that bring the signal of
 * `fromPortID` to `toPortID` through the effective wiring. Each suggestion
 * is a set of crosspoints (Videohub) or aux sources (ATEM) to set, at most
 * `maxHops` routers deep (default 2), never through the same router twice.
 * Several suggestions are all returned (fewest changes first), never
 * guessed between. `toPortID` may also be a router output itself.
 *
 * With `routes`, suggestions that are already set are marked `active`.
 */
export function findRoutes(fromPortID, toPortID, wiring, routes, options = {}) {
    const index = new TraceIndex(wiring);
    const maxHops = options.maxHops ?? 2;
    const maxSuggestions = options.maxSuggestions ?? 50;
    const from = index.portByID.get(fromPortID);
    const to = index.portByID.get(toPortID);
    if (!from || !to || from.id === to.id)
        return [];
    const state = routes ?? {};
    // Where the signal enters the first router
    let entry;
    const firstHops = [index.portHop(from, state)];
    if (index.arriving(from, false) && from.control) {
        entry = from;
    }
    else if (!index.arriving(from, false)) {
        const link = wiring.linkByPort.get(from.id);
        if (!link || link.fromPortID !== from.id)
            return [];
        entry = index.portByID.get(link.toPortID);
        if (!entry)
            return [];
        const hop = index.portHop(entry, state, "link");
        hop.link = link;
        firstHops.push(hop);
        if (entry.id === to.id) {
            // Already cabled directly
            return [{ changes: [], hops: firstHops, active: true }];
        }
    }
    if (!entry)
        return [];
    const suggestions = [];
    const search = (input, hops, changes, routers) => {
        if (suggestions.length >= maxSuggestions)
            return;
        if (changes.length >= maxHops)
            return;
        if (routers.has(input.deviceID))
            return;
        const nextRouters = new Set(routers).add(input.deviceID);
        const outputs = (index.portsByDevice.get(input.deviceID) ?? [])
            .filter((output) => output.direction !== "input")
            .sort((a, b) => (a.group ?? "").localeCompare(b.group ?? "") || a.number - b.number);
        for (const output of outputs) {
            const change = routeChange(index, input, output);
            if (!change)
                continue;
            const outHop = index.portHop(output, state, "route");
            outHop.routePath = change.path;
            const nextChanges = [...changes, change];
            if (output.id === to.id) {
                suggestions.push({
                    changes: nextChanges,
                    hops: [...hops, outHop],
                    active: false,
                });
                continue;
            }
            const link = wiring.linkByPort.get(output.id);
            if (!link || link.fromPortID !== output.id)
                continue;
            const next = index.portByID.get(link.toPortID);
            if (!next)
                continue;
            const inHop = index.portHop(next, state, "link");
            inHop.link = link;
            if (next.id === to.id) {
                suggestions.push({
                    changes: nextChanges,
                    hops: [...hops, outHop, inHop],
                    active: false,
                });
                continue;
            }
            if (next.control) {
                search(next, [...hops, outHop, inHop], nextChanges, nextRouters);
            }
        }
    };
    search(entry, firstHops, [], new Set());
    for (const suggestion of suggestions) {
        suggestion.active =
            !!routes &&
                suggestion.changes.every((change) => numberValue(state, change.deviceID, change.path) === change.value);
    }
    return suggestions.sort((a, b) => Number(b.active) - Number(a.active) ||
        a.changes.length - b.changes.length);
}
// ─── Text ─────────────────────────────────────────────────────
/** "VH in 21 "IO OUT 1"" style short name of a hop, for text and tests. */
export function hopLabel(hop) {
    if (hop.kind === "port") {
        const label = portLabel(hop.port);
        const device = hop.device?.name;
        const hardware = hop.hardwareLabel && hop.hardwareLabel !== hop.port.name
            ? ` "${hop.hardwareLabel}"`
            : "";
        return `${device ? `${device} ` : ""}${label}${hardware}`;
    }
    if (hop.kind === "internal") {
        return `${hop.device ? `${hop.device.name} ` : ""}${hop.name}`;
    }
    return `(${hop.text})`;
}
/**
 * A chain as one line, in signal-flow order ("DL1 → VH in 21 → [route] →
 * VH out 6 → ATEM in 6"), or reversed for pickers ("DL7 ← VH out 29 ←
 * ATEM Programm 2").
 */
export function formatPath(hops, options = {}) {
    const label = options.label ?? hopLabel;
    const parts = [];
    hops.forEach((hop, i) => {
        if (i > 0) {
            const via = hop.kind === "port" || hop.kind === "internal" ? hop.via : "origin";
            parts.push(via === "route" ? "[route]" : "");
        }
        parts.push(label(hop));
    });
    const items = parts.filter((part, i) => part !== "" || i === 0);
    if (options.reverse)
        return items.reverse().join(" ← ");
    return items.join(" → ");
}
